import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LINK_ALREADY_USED_EVENT,
  SESSION_EXPIRED_EVENT,
  setWidgetLinkId,
} from "../services/api";
import { useSessionStore } from "../store/useSessionStore";
import { useStore } from "../store/globalState";
import { APP_ROUTES } from "../constants";
import { LINK_ERROR_CODE } from "../utils/commons";
import {
  applyDeadLinkPartnerRecovery,
  partnerRecoveryFromErrorDetails,
} from "../utils/dead-link-recovery";
import {
  isConsumedReadonlyPath,
  resolveConsumedResumePath,
} from "../utils/consumed-resume";
import { useRouteWithToken } from "./useRouteWithToken";

/**
 * Listens for dead-link events (LINK_NOT_FOUND / LINK_EXPIRED / LINK_REVOKED /
 * 410). Expired/revoked go to SessionExpiredPage (partner return CTA when
 * configured). LINK_NOT_FOUND stays on ErrorPage with no CTA.
 */
export function useSessionExpiredHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { redirectTo, linkId } = useRouteWithToken();
  const resetSession = useSessionStore((state) => state.resetSession);
  const setLinkId = useStore((state) => state.setLinkId);
  const updateSession = useSessionStore((state) => state.updateSession);

  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      if (
        location.pathname === APP_ROUTES.ERROR_PAGE ||
        location.pathname === APP_ROUTES.SESSION_EXPIRED_PAGE ||
        location.pathname === APP_ROUTES.LINK_ALREADY_USED_PAGE
      ) {
        return;
      }

      const detail = (
        event as CustomEvent<{ code?: string; details?: unknown }>
      ).detail;
      const isRevoked = detail?.code === LINK_ERROR_CODE.LINK_REVOKED;
      const isNotFound = detail?.code === LINK_ERROR_CODE.LINK_NOT_FOUND;
      const errorCode = isRevoked
        ? LINK_ERROR_CODE.LINK_REVOKED
        : isNotFound
          ? LINK_ERROR_CODE.LINK_NOT_FOUND
          : LINK_ERROR_CODE.LINK_EXPIRED;

      applyDeadLinkPartnerRecovery(
        partnerRecoveryFromErrorDetails(detail?.details),
      );

      resetSession();
      setWidgetLinkId(null);
      setLinkId(null);

      if (isNotFound) {
        navigate(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: {
            title: t("error.linkExpired"),
            subTitle: null,
            message: t("error.linkExpiredMessage"),
            btnText: null,
            isOut: false,
            errorCode,
          },
        });
        return;
      }

      navigate(APP_ROUTES.SESSION_EXPIRED_PAGE, { replace: true });
    };

    const handleAlreadyUsed = (event: Event) => {
      if (
        location.pathname === APP_ROUTES.LINK_ALREADY_USED_PAGE ||
        isConsumedReadonlyPath(location.pathname)
      ) {
        return;
      }
      const detail = (
        event as CustomEvent<{ details?: { returnToChatUrl?: string; operationId?: string } }>
      ).detail;
      const current = useSessionStore.getState().session.data;
      const operationId =
        detail?.details?.operationId ?? current?.config?.usage?.operationId;
      if (current) {
        const returnToChatUrl =
          detail?.details?.returnToChatUrl ?? current.config?.returnToChatUrl;
        updateSession({
          ...current,
          config: {
            ...current.config,
            returnToChatUrl,
            usage: { state: "consumed", operationId },
          },
        });
      }
      const sessionLinkId = current?.linkId ?? linkId;
      if (!sessionLinkId) {
        redirectTo(APP_ROUTES.LINK_ALREADY_USED_PAGE, { replace: true });
        return;
      }
      void resolveConsumedResumePath({
        linkId: sessionLinkId,
        operationId,
      }).then((path) => {
        if (isConsumedReadonlyPath(location.pathname)) return;
        redirectTo(path, { replace: true });
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener(LINK_ALREADY_USED_EVENT, handleAlreadyUsed);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener(LINK_ALREADY_USED_EVENT, handleAlreadyUsed);
    };
  }, [
    navigate,
    location.pathname,
    t,
    resetSession,
    setLinkId,
    updateSession,
    redirectTo,
    linkId,
  ]);
}
